import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { WorkflowItem } from "@/api/models"

import { Copy, History, MoreVertical, Pencil, Share2, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { FlowShareDialog } from "./flow-share-dialog"
import { WorkflowRunsDialog } from "./workflow-runs-dialog"

export type WorkflowTileDropdownProps = {
  workflow: WorkflowItem
  canEdit: boolean
  onOpen: (workflow: WorkflowItem) => void
  onDuplicate: (workflow: WorkflowItem) => void
  onDelete: (workflow: WorkflowItem) => void
  editLabel: string
  duplicateLabel: string
  deleteLabel: string
  moreActionsLabel: string
  onOpenChange?: (open: boolean) => void
}

export function WorkflowTileDropdown({
  workflow,
  canEdit,
  onOpen,
  onDuplicate,
  onDelete,
  editLabel,
  duplicateLabel,
  deleteLabel,
  moreActionsLabel,
  onOpenChange,
}: WorkflowTileDropdownProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const [runsOpen, setRunsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  return (
    <>
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={moreActionsLabel}
            className="text-muted-foreground h-7 w-7 p-0"
            size="sm"
            variant="ghost"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              onOpen(workflow)
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            {editLabel}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setRunsOpen(true)
            }}
          >
            <History className="mr-2 h-4 w-4" />
            {t("runs.menuItem", "Run history")}
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setShareOpen(true)
              }}
            >
              <Share2 className="mr-2 h-4 w-4" />
              {t("share.menuItem", "Share")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              onDuplicate(workflow)
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            {duplicateLabel}
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(workflow)
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleteLabel}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* The tile card has an onClick that navigates into the editor. Radix
          portals these dialogs in the DOM, but React synthetic events still
          bubble through the React tree — so clicks inside the dialog would
          otherwise reach the card and navigate away (breaking multi-step flows
          like share: select-then-save). Stop propagation at the dialog root. */}
      <div role="presentation" onClick={(e) => e.stopPropagation()}>
        <WorkflowRunsDialog
          open={runsOpen}
          workflowId={workflow.id}
          workflowTitle={workflow.title}
          onOpenChange={setRunsOpen}
        />
        <FlowShareDialog
          initialParticipants={workflow.participants}
          open={shareOpen}
          workflowId={workflow.id}
          workflowTitle={workflow.title}
          onOpenChange={setShareOpen}
        />
      </div>
    </>
  )
}
